import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import { SPRING_SWAP } from '@/lib/ease';
import { BouncyDisclosurePanel } from '@/components/motion/bouncy-accordion';
import { ThinkingWords } from '@/components/agents/loading-states/thinking-words';
import { StepStatusIcon } from '@/components/agents/step-status-icon';

/**
 * Die agentischen Zwischenschritte einer Bot-Nachricht als **eine** Leiste im
 * Stil von Vercels open-agents: eine einzeilige, ruhige Zusammenfassung, die
 * sich aufklappen lässt — keine Karte, kein Fortschrittsbalken, keine
 * Signalfarben. Reine Darstellung; die Schritte kommen als Roh-HTML im
 * gestreamten Markdown und werden in `splitAgentStepSegments` herausgelöst.
 *
 * Nur zwei Zustände: Was gestreamt wurde, ist gelaufen. Der letzte Schritt läuft
 * noch, solange die Nachricht auf ihre Antwort wartet — alles davor ist fertig.
 * Einen Vorrat noch nicht begonnener Schritte kennt der Stream nicht.
 *
 * **Der Zustand führt, der Klick korrigiert.** Solange gearbeitet wird, steht
 * die Leiste offen; mit dem letzten Schritt klappt sie von selbst zu. Danach
 * entscheidet die Leserin per Klick.
 *
 * **Bewegung nur bei echter Veränderung.** Animiert wird, was *während* des
 * Lesens passiert: eine neu dazugekommene Zeile, das Auf- und Zuklappen. Beim
 * Öffnen einer alten Unterhaltung steht die Leiste still — sonst flackern beim
 * Scrollen alle Schritte aller Nachrichten gleichzeitig auf. Jede bewegte
 * Stelle trägt `motion-reduce:*`, respektiert also `prefers-reduced-motion`.
 *
 * Das Markup lebt im `.markdown`-Container, dessen Regeln auf fremdes DOM
 * zielen (`ul`, `ol`, `li`, `p`, `a`, `table`). Deshalb hier ausschließlich
 * `div`/`span` — eine Liste würde die Aufzählungspunkte des Markdowns erben,
 * und der Umschalter ist deshalb ein `div` mit Schalter-Rolle statt `button`.
 *
 * **Warum nicht `AgentActivity`.** Die beui-Aktivitätsanzeige bringt eine
 * andere Anatomie mit: im laufenden Zustand *keinen* Umschalter (nur eine
 * Statuszeile), ein Sichtfenster fester Höhe mit eigener Bildlaufleiste und
 * Verlaufsmaske, und Zeilen, die keine eigenen Attribute annehmen. Damit
 * fielen `aria-expanded` während des Laufs, `data-progress-step` je Zeile und
 * die ruhige einzeilige Leiste weg. Übernommen ist deshalb nur der
 * Laufend-Zustand: `ThinkingShimmer` über dem Etikett.
 */
interface ChatAgentStepsProps {
    steps: string[];
    /** Der letzte Schritt läuft noch. */
    running?: boolean;
}

export const ChatAgentSteps = ({ steps, running = false }: ChatAgentStepsProps) => {
    const { t } = useTranslation();
    const reduce = useReducedMotion();

    // Standardmäßig zu, auch während des Laufs: der Stand oben (wechselnde
    // Begriffe mit dem laufenden Schritt, Punkte je Schritt) genügt; die
    // Liste öffnet, wer sie sehen will.
    const [open, setOpen] = React.useState(false);

    // Was beim ersten Rendern schon da war, gilt als bekannt: nur später
    // dazugekommene Zeilen blenden sich ein.
    const revealedRef = React.useRef<number | null>(null);
    const revealedBefore = revealedRef.current ?? steps.length;
    const wasRunningRef = React.useRef(running);
    // Born while the run is going: it takes over from the "Denke nach …"
    // indicator, so it glides in instead of popping up. Bars of finished
    // answers (history) just stand.
    const bornRunningRef = React.useRef(running);

    React.useEffect(() => {
        revealedRef.current = steps.length;
    }, [steps.length]);

    // Endet der Lauf, klappt eine offen gelassene Liste wieder zu. Sonst bleibt
    // sie, wo der Klick sie hingestellt hat.
    React.useEffect(() => {
        if (wasRunningRef.current === running) return;
        wasRunningRef.current = running;
        if (!running) setOpen(false);
    }, [running]);

    if (steps.length === 0) return null;

    const label = running
        ? t('chatMessages.agentStepsRunningLabel')
        : t('chatMessages.agentStepsDoneLabel', { count: steps.length });

    const toggle = () => setOpen(previous => !previous);

    return (
        <div
            data-testid='chat-agent-steps'
            data-agent-steps='true'
            // Working notes, not part of the answer: selecting the answer with
            // the mouse must not drag them into the clipboard.
            // `!overflow-visible` hier, an der Kopfzeile und am Punkt: Der Chat
            // setzt per Wildcard (`.chatBot .messagesContainer * { overflow-x:
            // hidden }` u. a.) auf jedes Element Beschnitt, und die Linie unter
            // dem Kopfpunkt ragt aus dessen 16-px-Kasten heraus.
            className={`my-1.5 w-full select-none !overflow-visible${bornRunningRef.current ? ' oa-step-enter' : ''}`}
        >
            <div
                role='button'
                tabIndex={0}
                aria-expanded={open}
                data-testid='chat-agent-steps-toggle'
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
                className='group inline-flex cursor-pointer items-center gap-2 !overflow-visible rounded-md py-0.5 text-[15px] tabular-nums text-[var(--oa-fg-3)] transition-colors hover:text-[var(--oa-fg)]'
            >
                <span className='relative flex size-4 items-center justify-center !overflow-visible' aria-hidden>
                    <span
                        className={`size-2.5 rounded-full ${
                            running
                                ? 'animate-pulse bg-[var(--oa-fg-2)] motion-reduce:animate-none'
                                : 'bg-[var(--oa-dot)]'
                        }`}
                    />
                    {/* Aufgeklappt hängt die Liste an diesem Punkt: die Linie
                        läuft von seiner Unterkante bis zur ersten Schrittzeile
                        (16 px, gemessen) und zieht sich beim Aufklappen auf.
                        Waagerecht über `left` gesetzt, nicht über `translate`:
                        Layout-Positionen rasten wie die Listenlinie auf Pixel
                        ein, ein Transform wird geglättet und stand sichtbar
                        daneben. */}
                    <span
                        className={`absolute left-[7.5px] top-[calc(50%+5px)] h-4 w-px origin-top bg-[var(--oa-border)] transition-transform duration-300 ease-out motion-reduce:transition-none ${
                            open ? 'scale-y-100' : 'scale-y-0'
                        }`}
                    />
                </span>
                {/* Laufend: wechselnde Begriffe unter dem Schimmer der
                    beui-Ladezustände (`ThinkingWords`), statt eines Wortes. */}
                {running
                    ? <ThinkingWords className='font-normal' fallback={label} tool={steps[steps.length - 1]} />
                    : <span>{label}</span>}
                {/* Trennpunkt und Schrittpunkte fassen die Liste zusammen — das
                    braucht es nur eingeklappt. Aufgeklappt steht die Liste selbst
                    da: die Gruppe fällt weich auf Breite null und blendet aus.
                    `marginLeft: -8` nimmt dabei den Flex-Abstand (`gap-2`) mit,
                    den sie sonst als Lücke vor dem Pfeil hinterließe. */}
                <motion.span
                    aria-hidden
                    data-testid='chat-agent-steps-dots'
                    className='flex items-center gap-2 overflow-hidden'
                    initial={false}
                    animate={open
                        ? { width: 0, opacity: 0, marginLeft: -8 }
                        : { width: 'auto', opacity: 1, marginLeft: 0 }}
                    transition={reduce ? { duration: 0 } : { duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                    <span className='text-[var(--oa-fg-5)]' aria-hidden>·</span>
                    {/* Ein Punkt je Schritt — der laufende bleibt blass, bis er steht.
                        Ein neuer Punkt wächst aus der Breite null heraus (Punkt plus
                        Abstand), so rückt der Pfeil dahinter weich nach, statt zu
                        springen; die Farbe geht blass → voll über. Punkte, die beim
                        Öffnen schon da sind, stehen einfach. */}
                    <span className='flex items-center' aria-hidden>
                        {steps.map((_step, index) => (
                            <motion.span
                                key={index}
                                initial={index >= revealedBefore && !reduce
                                    ? { width: 0, opacity: 0, scale: 0 }
                                    : false}
                                animate={{ width: index === 0 ? 5 : 8, opacity: 1, scale: 1 }}
                                transition={SPRING_SWAP}
                                className='flex shrink-0 justify-end'
                            >
                                <span
                                    className={`size-[5px] shrink-0 rounded-full transition-colors duration-500 ease-out motion-reduce:transition-none ${
                                        running && index === steps.length - 1
                                            ? 'bg-[var(--oa-fg-5)]'
                                            : 'bg-[var(--oa-fg-2)]'
                                    }`}
                                />
                            </motion.span>
                        ))}
                    </span>
                </motion.span>
                <ChevronRight
                    aria-hidden
                    className={`size-3.5 text-[var(--oa-fg-5)] transition-transform duration-200 ease-out motion-reduce:transition-none${
                        open ? ' rotate-90' : ''
                    }`}
                />
            </div>
            {/* Zusammenklappen federt — dieselbe Physik, mit der die
                Ergebnis-Kacheln unter der Antwort aufgehen. Die Höhe wird
                gemessen, damit die Feder eine Zahl hat, über die sie
                hinausschwingen kann. */}
            <BouncyDisclosurePanel open={open} reduce={reduce} innerClassName='mt-1.5'>
                <div>
                    {steps.map((step, index) => {
                        const isRunning = running && index === steps.length - 1;
                        const isLast = index === steps.length - 1;
                        const isNew = index >= revealedBefore;
                        return (
                            <div
                                key={index}
                                data-testid='chat-agent-step'
                                data-progress-step={isRunning ? 'running' : 'done'}
                                // `pl-px`: Mitte des 14-px-Statuskreises = Mitte des Kopfpunkts (8 px).
                                className={`flex gap-2 pl-px${isNew ? ' oa-step-enter' : ''}`}
                            >
                                {/* Eine durchgehende Linie: ein Stück über dem Punkt
                                    (bis zum Statuskreis, mittig zur ersten Textzeile), eines darunter
                                    bis zum Zeilenende — ohne Lücken, so schließen die
                                    Zeilen aneinander und die erste an den Kopfpunkt
                                    an. Die letzte endet an ihrem Punkt. */}
                                <div className='flex flex-col items-center' aria-hidden>
                                    <span
                                        className={`h-[9px] w-px shrink-0 origin-top bg-[var(--oa-border)]${
                                            isNew && index > 0 ? ' oa-line-draw' : ''
                                        }`}
                                    />
                                    {/* Läuft: drehender Bogen; fertig: schließt er sich zum
                                        Kreis, und das Häkchen zeichnet sich hinein. */}
                                    <StepStatusIcon
                                        done={!isRunning}
                                        className={isRunning ? 'text-[var(--oa-fg-2)]' : 'text-[var(--oa-fg-4)]'}
                                    />
                                    {/* Unter dem Punkt: steht, sobald eine Zeile folgt, und
                                        zieht sich dann von oben auf (`scale-y`), statt
                                        einfach da zu sein. Das Stück über dem Punkt der
                                        neuen Zeile zeichnet sich im Anschluss. */}
                                    <span
                                        className={`w-px flex-1 origin-top bg-[var(--oa-border)] transition-transform duration-300 ease-out motion-reduce:transition-none ${
                                            isLast ? 'scale-y-0' : 'scale-y-100'
                                        }`}
                                    />
                                </div>
                                <div
                                    className={`py-1 text-[15px] leading-6 ${
                                        isRunning ? 'text-[var(--oa-fg)]' : 'text-[var(--oa-fg-3)]'
                                    }`}
                                >
                                    {step}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </BouncyDisclosurePanel>
        </div>
    );
};
